/**
 **@author       yontaek@zumper.com (Yon Taek Chung)
 **@copyright    Copyright (c) 2024, Zumper
 */

import { Flex, Heading, Image, Text } from '@chakra-ui/react'
import { GreenCheckMark } from '@zcode-ui/icons'
import {
  APP_DOWNLOAD_CLICK,
  APP_DOWNLOAD_IMPRESSION,
  event,
} from '@zcode-ui/web-zumper-integrations'
import { AppStoreLink, PlayStoreLink } from '@zcode-ui/zumper-footer'
import { buildSrc } from '@zcode/image-cdn-utils'
import { EVENT_NAMES, HOMEPAGE, PAGE_NAMES, PROP_NAMES, track } from '@zcode/mixpanel'
import { AnalyticsC, Origin, trackGAEvent } from '@zcode/nr-utils'
import React from 'react'
import { useDispatch } from 'react-redux'
import { DOWNLOAD_APP } from '../analytics'
import { useHomepageImpression } from '../hooks/useHomepageImpression'
import appImage from './img/appimage.webp'

const IOS = 'iOS'
const ANDROID = 'Android'

const AboutApp = () => {
  const ref = useHomepageImpression({
    name: APP_DOWNLOAD_IMPRESSION,
    mixpanelSection: HOMEPAGE.APP_DOWNLOAD,
  })
  const onIosClick = () => trackGAEvent(AnalyticsC.HOMEPAGE, DOWNLOAD_APP, IOS)
  const onAndroidClick = () => trackGAEvent(AnalyticsC.HOMEPAGE, DOWNLOAD_APP, ANDROID)
  const dispatch = useDispatch()
  const onAppDownloadClick = (platform: 'iOS' | 'Android') => {
    dispatch(event({ name: APP_DOWNLOAD_CLICK, props: { origin: Origin.HOMEPAGE } }))
    track({
      event: EVENT_NAMES.CLICK,
      payload: {
        [PROP_NAMES.PAGE_NAME]: PAGE_NAMES.HOMEPAGE,
        [PROP_NAMES.BUTTON_CLICKED]: 'Download App',
      },
    })
    platform === IOS ? onIosClick() : onAndroidClick()
  }

  return (
    <Flex
      alignItems="center"
      flexDir={{ base: 'column', desktop: 'row' }}
      gap={{ xl: 6 }}
      ref={ref}
    >
      <Image
        alt=""
        height={{ base: '322px', desktop: '265px', lg: '380px', xl: '506px' }}
        flexShrink={0}
        loading="eager"
        src={buildSrc(appImage, {
          fit: 'clip',
          h: 588,
          w: 506,
        })}
        width={{ base: '375px', desktop: '308px', lg: '434px', xl: '588px' }}
      />
      <Flex flexDir="column" px={{ lg: 8, xl: 12 }} w="100%">
        <Heading<'h2'> as="h2" mb={4} variant="display28To32">
          A best-in-class experience
        </Heading>
        <Text color="gray.950" variant="body16Reg" w={{ lg: '100%', xl: '492px' }}>
          The Zumper iOS and Android apps provide a seamless apartment search experience.
        </Text>
        <Flex flexDir="column" gap={2} mt={10} mb={{ base: 10, desktop: 14 }}>
          <Flex alignItems="center" gap={2}>
            <GreenCheckMark size={20} />
            <Text fontSize="md" fontWeight="bold" lineHeight={6} color="gray.950">
              Extensive filters to refine your search
            </Text>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <GreenCheckMark size={20} />
            <Text fontSize="md" fontWeight="bold" lineHeight={6} color="gray.950">
              Tailored recommendations
            </Text>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <GreenCheckMark size={20} />
            <Text fontSize="md" fontWeight="bold" lineHeight={6} color="gray.950">
              Alerts to track pricing and availability
            </Text>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <GreenCheckMark size={20} />
            <Text fontSize="md" fontWeight="bold" lineHeight={6} color="gray.950">
              Digital tours and applications
            </Text>
          </Flex>
        </Flex>
        <Flex gap={5}>
          <AppStoreLink onClick={() => onAppDownloadClick(IOS)} origin="homepage" />
          <PlayStoreLink onClick={() => onAppDownloadClick(ANDROID)} origin="homepage" />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default AboutApp
